import React, { useCallback, useEffect, useState } from 'react';
import className from 'classnames';
import { FormattedMessage } from 'react-intl';
import { useQuery } from '@apollo/client';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth';
import { useCanManageTemplates } from '../../auth/useCanManageTemplates';
import { useUserRestrictions } from '../../auth/user-restrictions';
import { useTeamInfo } from '../../team-id-context';
import { usePartyIds, queries } from '../../api';
import { hasUserTeamOwnedOrganization } from '../../api/queries';
import { get as getLatestTeamId, set as setLatestTeamId } from '../../utils/latest-team-id';
import { Item } from './MobileItem';
import { ReactComponent as DealsIcon } from '../../shared/icons/folder_lock_light_icon.svg';
import { ReactComponent as TeamIcon } from '../../shared/icons/team-icon.svg';
import { ReactComponent as HelpIcon } from '../../shared/icons/help-icon.svg';
import { ReactComponent as SignoutIcon } from '../../shared/icons/sign-out-icon.svg';
import { ReactComponent as TemplateIcon } from '../../shared/icons/icon-template.svg';
import { ReactComponent as ClausesIcon } from '../../shared/icons/icon-clauses.svg';
import { ReactComponent as SettingsIcon } from '../../shared/icons/gear-icon.svg';
import { ReactComponent as ChevronDownIcon } from '../../shared/icons/chevron-down.svg';
import { ReactComponent as ChevronUpIcon } from '../../shared/icons/chevron-up.svg';
import { ReactComponent as TrackerIcon } from '../../shared/icons/clock-icon.svg';
import { usePermissions } from '../../contexts/PermissionsContext';
import { useDocumentState } from '../../contexts/DocumentContext';

import { A, fillPlaceholders, paths } from '../../routing';
import { SubscriptionRequiredModal } from '../../pages';
import SignOutConfirmationModal from '../private-layout/SignOutConfirmationModal';
import { ReactComponent as LogoIcon } from '../../shared/icons/logo-icon.svg';
import styles from '../private-layout/styles.module.scss';
import CurrentWorkspaceIndicatorMobile from './current-user-indicator-mobile';
import MobileSidebar from './mobile-sidebar';

export const MobileMenu = ({ isOpen, onClose }) => {
  const [{ user }, { logout }] = useAuth();
  const navigate = useNavigate();
  const { resetDocumentState } = useDocumentState();
  const { setTeamPermissions } = usePermissions();
  const canManageChecklists = useCanManageTemplates();
  const { pathname: path } = useLocation();
  const { restrictionsLoading, hasAccessToTemplates, hasAccessToClauses, canCreateTemplates } = useUserRestrictions();
  const [showSubscriptionRequiredModal, setSubscriptionRequiredModal] = useState(false);
  const [showSignOutModal, setShowSignOutModal] = useState(false);
  const teamId = useTeamInfo().teamId ?? getLatestTeamId(user?.id);
  const { loading: partyIdsLoading, partyIds } = usePartyIds();
  const { loading: teamLoading, data: teamData } = useQuery(queries.getTeamById, {
    skip: teamId == null,
    variables: { teamId },
  });

  const loading = partyIdsLoading || teamLoading || restrictionsLoading;
  const partyId = loading ? null : (teamData?.teamById.party.id ?? partyIds.tenant);
  const isSettingsActive =
    paths.settings._catchallRegex?.test(path) ?? path === fillPlaceholders(paths.settings.organizations, { teamId });
  const [showSettingsSubnav, setShowSettingsSubnav] = useState(isSettingsActive);

  // Query for organization data to determine if Org Requests should show
  const { data: organizationsData } = useQuery(hasUserTeamOwnedOrganization, {
    skip: !teamId,
    variables: { teamId },
  });

  useEffect(() => {
    if (teamId == null) return;
    setLatestTeamId(user?.id, teamId);
  }, [teamId, partyId, partyIds, user]);

  const handleLogout = useCallback(() => {
    setShowSignOutModal(true);
  }, []);

  const confirmSignOut = useCallback(
    (data) => {
      setTeamPermissions([]);
      logout(data).then(() => {
        navigate(paths.auth.login);
        resetDocumentState();
      });
      setShowSignOutModal(false);
    },
    [logout, resetDocumentState, navigate, setTeamPermissions]
  );

  const cancelSignOut = useCallback(() => {
    setShowSignOutModal(false);
  }, []);

  return (
    <MobileSidebar
      isOpen={isOpen}
      onClose={onClose}
      header={
        <A href={paths.home} aria-label="Home" className={styles.logoIcon}>
          <LogoIcon width={64} height={64} />
          PropDocs
        </A>
      }
    >
      <div className={styles.mobileMenu__children}>
        {teamId ? (
          <Item
            href={paths.deals.landing}
            hrefParams={{ teamId }}
            pathRegex={paths.deals._catchallRegex}
            icon={DealsIcon}
            qa="main-nav-deals"
          >
            <FormattedMessage id="nav.deals" defaultMessage="Deals" />
          </Item>
        ) : (
          <Item href={paths.home} icon={DealsIcon}>
            <FormattedMessage id="nav.deals" defaultMessage="Deals" />
          </Item>
        )}
        {/* Tracker - only show for premium users */}
        {canCreateTemplates && (
          <Item
            href={paths.tracker.landing}
            hrefParams={{ teamId }}
            pathRegex={paths.tracker._catchallRegex}
            icon={TrackerIcon}
            qa="main-nav-tracker"
          >
            <FormattedMessage id="nav.tracker" defaultMessage="Tracker" />
          </Item>
        )}
        <Item
          href={paths.team.landing}
          hrefParams={{ teamId }}
          pathRegex={paths.team._catchallRegex}
          icon={TeamIcon}
          qa="main-nav-team"
        >
          <FormattedMessage id="nav.people" defaultMessage="People" />
        </Item>
        {hasAccessToTemplates ? (
          <Item
            href={paths.templates.landing}
            hrefParams={{ teamId }}
            pathRegex={paths.templates._catchallRegex}
            icon={TemplateIcon}
            qa="main-nav-templates"
          >
            <FormattedMessage id="nav.templates" defaultMessage="Templates" />
          </Item>
        ) : (
          <span className={styles.mobileMenu__item} onClick={() => setSubscriptionRequiredModal(true)}>
            <TemplateIcon height="25" width="25" className="ml-1" />
            <div className={styles.mobileMenu__itemLabel}>
              <FormattedMessage id="nav.templates" defaultMessage="Templates" />
            </div>
          </span>
        )}
        {hasAccessToClauses ? (
          <Item
            href={paths.clauses.landing}
            hrefParams={{ teamId }}
            pathRegex={paths.clauses._catchallRegex}
            icon={ClausesIcon}
            qa="main-nav-clauses"
          >
            <FormattedMessage id="nav.clauses" defaultMessage="Clauses" />
          </Item>
        ) : (
          <span className={styles.mobileMenu__item} onClick={() => setSubscriptionRequiredModal(true)}>
            <ClausesIcon height="25" width="25" className="ml-1" />
            <div className={styles.mobileMenu__itemLabel}>
              <FormattedMessage id="nav.clauses" defaultMessage="Clauses" />
            </div>
          </span>
        )}
        <div>
          <span
            className={className(styles.mobileMenu__item, { [styles.isCurrent]: isSettingsActive })}
            onClick={() => setShowSettingsSubnav(!showSettingsSubnav)}
            data-testid="main-nav-settings"
            style={{ cursor: 'pointer' }}
          >
            <SettingsIcon height="25" width="25" className="ml-1 mr-3" />
            <div style={{ flex: 1 }}>
              <FormattedMessage id="nav.settings" defaultMessage="Settings" />
            </div>
            {showSettingsSubnav ? (
              <ChevronUpIcon width="16" height="16" style={{ marginLeft: 'auto' }} />
            ) : (
              <ChevronDownIcon width="16" height="16" style={{ marginLeft: 'auto' }} />
            )}
          </span>
          {showSettingsSubnav && (
            <div style={{ paddingLeft: '48px' }}>
              <A
                href={paths.settings.organizations}
                hrefParams={{ teamId }}
                className={styles.mobileSubnavItem}
                data-testid="settings-nav-organizations"
                style={{ display: 'block', padding: '12px 16px', textDecoration: 'none', color: '#6F747E' }}
              >
                <FormattedMessage id="nav.organizations" defaultMessage="Organizations" />
              </A>
              {organizationsData?.teamById?.organization && (
                <A
                  href={paths.settings.requests}
                  hrefParams={{ teamId }}
                  className={styles.mobileSubnavItem}
                  data-testid="settings-nav-requests"
                  style={{ display: 'block', padding: '12px 16px', textDecoration: 'none', color: '#6F747E' }}
                >
                  <FormattedMessage id="nav.requests" defaultMessage="Org Requests" />
                </A>
              )}
              {canManageChecklists && (
                <A
                  href={paths.settings.checklistTemplates}
                  hrefParams={{ teamId }}
                  className={styles.mobileSubnavItem}
                  data-testid="settings-nav-checklists"
                  style={{ display: 'block', padding: '12px 16px', textDecoration: 'none', color: '#6F747E' }}
                >
                  <FormattedMessage id="nav.checklists" defaultMessage="Checklists" />
                </A>
              )}
              <A
                href={paths.account}
                hrefParams={{}}
                className={styles.mobileSubnavItem}
                data-testid="settings-nav-account"
                style={{ display: 'block', padding: '12px 16px', textDecoration: 'none', color: '#6F747E' }}
              >
                <FormattedMessage id="nav.account" defaultMessage="Account" />
              </A>
            </div>
          )}
        </div>
        <a href="/help" target="_blank" rel="noopener noreferrer" className={styles.mobileMenu__item} data-testid="main-nav-help">
          <HelpIcon height="25" width="25" className="ml-1 mr-3" />
          <div>
            <FormattedMessage id="nav.help" defaultMessage="Help" />
          </div>
        </a>
        <span onClick={handleLogout} className={className(styles.mobileMenu__item)}>
          <SignoutIcon height="25" width="25" className="ml-1 mr-3" />
          <div>
            <FormattedMessage id="nav.sign_out" defaultMessage="Sign Out" />
          </div>
        </span>
        <CurrentWorkspaceIndicatorMobile />
        <SubscriptionRequiredModal
          isOpen={showSubscriptionRequiredModal}
          onClose={() => setSubscriptionRequiredModal(false)}
        />
        <SignOutConfirmationModal show={showSignOutModal} onClose={cancelSignOut} onConfirm={confirmSignOut} />
      </div>
    </MobileSidebar>
  );
};
